import { computed, type IComputedValue, type IObservableValue, observable, reaction, runInAction } from "mobx"

import { type OrgContext, withGlobalLoader } from "@/lib/context.ts"
import { SECRET_VALUE_PLACEHOLDER } from "@/lib/displayUtils.ts"
import { resetErrorOnChange, submitGeneric } from "@/lib/formUtils.ts"
import { Model } from "@/lib/Model.ts"

import type {
  AiChatMessageContent,
  ProjectSecret,
  ProjectSecretWithValue
} from "@/lib/client/generated/TypeDefinitions.ts"
import type { ChangeEvent } from "react"

export class SecretViewModel extends Model {
  readonly value = observable.box("")
  readonly key: string
  readonly description: string | null
  readonly error = observable.box<string | null>(null)
  readonly disabled = observable.box(false)
  readonly changed = observable.box(false)

  constructor(
    secret: {
      key: string,
      description: string | null,
      skipped: boolean | null,
      value?: string
    },
    readonly isEditing: IObservableValue<boolean>,
    readonly isManuallyEnteredEditMode: IObservableValue<boolean>
  ) {
    super("SecretViewModel")
    this.description = secret.description
    this.key = secret.key
    if (secret.value) {
      this.value.set(secret.value)
      this.changed.set(true)
    }
    this.disabled.set(!!secret.skipped)
    resetErrorOnChange(this.lifetime, this.error, this.value)
    resetErrorOnChange(this.lifetime, this.error, this.disabled)
    this.lifetime.add(reaction(
      () => this.isManuallyEnteredEditMode.get(),
      (isManuallyEnteredEditMode) => {
        if (isManuallyEnteredEditMode) {
          this.changed.set(false)
          this.value.set(SECRET_VALUE_PLACEHOLDER)
        }
      }
    ), "SecretViewModel:isEditing")
  }

  toDto = () => {
    return {
      key: this.key,
      value: !this.changed.get() || this.disabled.get() ? "" : this.value.get(),
      description: this.description,
      skipped: this.disabled.get(),
      changed: this.changed.get()
    } satisfies ProjectSecretWithValue
  }

  validate = () => {
    if (this.disabled.get()) {
      return
    }

    if (this.isManuallyEnteredEditMode.get() && !this.changed.get()) {
      return
    }

    runInAction(() => {
      if (this.value.get().length === 0) {
        this.error.set("Secret value is required")
      }
    })
  }

  setDisabled = (disabled: boolean) => {
    runInAction(() => {
      if (!this.changed.get() && this.isManuallyEnteredEditMode.get()) {
        this.value.set("")
      }
      this.changed.set(true)
      this.disabled.set(disabled)
    })
  }

  handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    runInAction(() => {
      this.changed.set(true)
      this.value.set(e.target.value)
    })
  }

  handleFocus = () => {
    runInAction(() => {
      if (!this.changed.get() && this.isManuallyEnteredEditMode.get()) {
        this.value.set("")
      }
    })
  }

  handleBlur = () => {
    runInAction(() => {
      if (!this.changed.get() && this.isManuallyEnteredEditMode.get()) {
        this.value.set(SECRET_VALUE_PLACEHOLDER)
      }
    })
  }
}

export class SecretsListViewModel extends Model {
  readonly isSubmitting = observable.box(false)
  readonly secrets = observable.box<SecretViewModel[]>([])
  readonly context: OrgContext
  readonly taskId: string
  readonly addSecretDialogOpen = observable.box(false)
  readonly confirmed = observable.box(false)
  readonly isInEditMode: IComputedValue<boolean>
  readonly isManuallyEnteredEditMode = observable.box(false)
  readonly canEnterEditMode: IComputedValue<boolean>
  readonly originalSecrets: ProjectSecret[]
  readonly processing: IComputedValue<boolean>
  readonly messageId: string | null

  readonly canSubmit = computed(() => {
    return this.secrets.get().every(it => it.error.get() === null) && !this.isSubmitting.get()
  })

  constructor(params: {
    taskId: string
    content: AiChatMessageContent.InitRuntimeConfig.SecretList
    context: OrgContext
    agentIsRunning: IObservableValue<boolean>,
    messageId: string | null
  }) {
    super("SecretsListViewModel")
    this.context = params.context
    this.taskId = params.taskId
    this.messageId = params.messageId
    this.isInEditMode = computed(() => {
      return !this.confirmed.get() || this.isManuallyEnteredEditMode.get() && this.canEnterEditMode.get()
    })
    this.canEnterEditMode = computed(() => {
      return this.confirmed.get() && !this.isSubmitting.get()
    })
    this.processing = computed(() => this.isSubmitting.get() || params.agentIsRunning.get())
    this.confirmed.set(params.content.confirmed)
    this.originalSecrets = params.content.secrets
    this.secrets.set(params.content.secrets.map(it => new SecretViewModel(it, this.isInEditMode, this.isManuallyEnteredEditMode)))
  }

  private setSecrets = (fn: (secrets: SecretViewModel[]) => SecretViewModel[]) => {
    runInAction(() => {
      this.secrets.set(fn(this.secrets.get()))
    })
  }

  setSecretDialogOpen = (value: boolean) => {
    runInAction(() => {
      this.addSecretDialogOpen.set(value)
    })
  }

  private validateSecrets = async () => {
    this.secrets.get().forEach(it => it.validate())
    for (const secret of this.secrets.get()) {
      const error = secret.error.get()
      if (error) {
        return error
      }
    }
    return null
  }

  handleAddSecret = async (key: string, description: string, value: string) => {
    this.setSecrets((secrets) => [...secrets, new SecretViewModel({
      key,
      description,
      value,
      skipped: false
    }, this.isInEditMode, this.isManuallyEnteredEditMode)])
  }

  readonly setIsEditing = (value: boolean) => {
    runInAction(() => {
      this.isManuallyEnteredEditMode.set(value)
    })
  }

  readonly reset = () => {
    this.setIsEditing(false)
    runInAction(() => {
      this.secrets.set(this.originalSecrets.map(it => new SecretViewModel(it, this.isInEditMode, this.isManuallyEnteredEditMode)))
    })
  }

  handleContinue = async () => {
    await submitGeneric({
      validate: this.validateSecrets,
      context: this.context,
      isSubmitting: this.isSubmitting,
      canSubmit: this.canSubmit,
      submit: async () => {
        const encodedSecrets = this.secrets.get()
          .map(it => it.toDto())
          .map(secret => ({
            ...secret,
            value: secret.value ? btoa(secret.value) : ""
          }))

        await withGlobalLoader(this.context, this.context.queries.apiClient.continuePreviewRuntimeConfigWithSecrets(this.taskId, encodedSecrets, this.messageId))
      }
    })
  }
}